/**
 * The week ahead — meals and training on one screen.
 *
 * This is the half the module was missing: a LOG answers "what did I do", a PLAN answers
 * "what am I doing". Planning a week of meals from a fixed library means the week's macros
 * are known on Sunday rather than discovered on Thursday, and it makes logging a tap
 * instead of a form, because the macros were reviewed once when the recipe was built.
 *
 * PLANNING IS NOT ONE SLOT AT A TIME ANY MORE. Cooking a tray of something and eating it
 * Monday, Tuesday and Wednesday is the normal case, and doing it a slot at a time was four
 * sheets and twelve taps. The picker asks which days, with the one you tapped already
 * ticked, so the one-day case costs exactly what it used to.
 *
 * WEEK NAVIGATION MATTERS MORE THAN IT LOOKS. Weeks run Monday-first, matching `weekStart`
 * in src/lib/api.ts and the meals module. That means on a Sunday "this week" is the week
 * that is ENDING — which is precisely when someone sits down to plan. So the next/previous
 * arrows are prominent and the header names the week in words rather than making you read
 * two dates and work it out.
 */
import { useState } from 'react';
import { useCallback, useEffect } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type {
  WellnessRecipe,
  WellnessSessionTypeDef,
  WellnessWeek,
  WellnessWeekDay,
} from '../../shared/types';
import { Card, Empty, ErrorState, Icon, LoadingRows } from '../home/ui';
import { SESSION_LABELS, macro, macroLabel } from './format';
import RecipePhoto from './RecipePhoto';
import Sheet from './Sheet';

/** Monday of the week containing `date`, matching the Worker and src/lib/api.ts. */
function weekStartOf(date: Date): string {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

function shift(start: string, days: number): string {
  const d = new Date(`${start}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const DAY_NAME = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
const DAY_NUM = (iso: string) => new Date(`${iso}T00:00:00`).getDate();

export default function WeekTab({ onChanged }: { onChanged: () => void }) {
  const [start, setStart] = useState(() => weekStartOf(new Date()));
  const [week, setWeek] = useState<WellnessWeek | null>(null);
  const [recipes, setRecipes] = useState<WellnessRecipe[]>([]);
  const [sessionTypes, setSessionTypes] = useState<WellnessSessionTypeDef[]>([]);
  const [error, setError] = useState(false);
  // Keyed `plan_date` to match the API exactly. It was `date`, spread straight into the
  // PUT body, so every pick 400'd on a missing plan_date — and silently, because the failure
  // was never surfaced. Naming it after the field it becomes removes the whole class of bug.
  const [picking, setPicking] = useState<{ plan_date: string; slot: string } | null>(null);
  const [planningTraining, setPlanningTraining] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planNote, setPlanNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [w, r, st] = await Promise.all([
        api.get<WellnessWeek>(`/wellness/week?start=${start}`),
        api.get<WellnessRecipe[]>('/wellness/recipes'),
        api.get<WellnessSessionTypeDef[]>('/wellness/session-types'),
      ]);
      setWeek(w);
      setRecipes(r);
      setSessionTypes(st);
    } catch {
      setError(true);
    }
  }, [start]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = () => {
    void load();
    onChanged();
  };

  const thisWeek = weekStartOf(new Date());
  const label =
    start === thisWeek ? 'This week' : start === shift(thisWeek, 7) ? 'Next week' : null;

  if (error)
    return (
      <Card className="p-5">
        <ErrorState what="your week" onRetry={() => void load()} />
      </Card>
    );
  if (!week)
    return (
      <Card className="p-5">
        <LoadingRows rows={6} />
      </Card>
    );

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setStart(shift(start, -7))}
          aria-label="Previous week"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          <Icon name="arrow" className="h-4 w-4 rotate-180" />
        </button>
        <div className="text-center">
          <p className="font-semibold text-ink">{label ?? `${week.start} – ${week.end}`}</p>
          {label && (
            <p className="text-xs text-ink-soft">
              {week.start.slice(5)} – {week.end.slice(5)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setStart(shift(start, 7))}
          aria-label="Next week"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          <Icon name="arrow" className="h-4 w-4" />
        </button>
      </div>

      {recipes.length === 0 && (
        <Card className="p-5">
          <Empty message="Your recipe library is empty. Build a couple of meals on the Meals tab and planning a week becomes picking from a shelf." />
        </Card>
      )}

      {planError && (
        <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
          {planError}
        </p>
      )}
      {planNote && <p className="rounded-xl bg-honey-soft px-3 py-2 text-sm text-honey">{planNote}</p>}

      {week.days.map((day) => (
        <DayCard
          key={day.date}
          day={day}
          slots={week.slots}
          targets={week.targets}
          isToday={day.date === today}
          sessionTypes={sessionTypes}
          onPick={(slot) => setPicking({ plan_date: day.date, slot })}
          onPlanTraining={() => setPlanningTraining(day.date)}
          onChanged={refresh}
        />
      ))}

      {picking && (
        <PlanPicker
          recipes={recipes}
          week={week}
          slot={picking.slot}
          date={picking.plan_date}
          onClose={() => setPicking(null)}
          onPlan={async (recipeId, slot, dates) => {
            // Surfaced, not swallowed. A planner that fails quietly looks like a planner
            // that ignores you, which is exactly how this shipped broken.
            setPlanError(null);
            setPlanNote(null);
            try {
              const res = await api.put<{ planned: string[]; skipped: string[] }>(
                '/wellness/plan/bulk',
                { recipe_id: recipeId, slot, dates }
              );
              if (res.skipped.length) {
                setPlanNote(
                  `Left ${res.skipped.map((d) => DAY_NAME(d)).join(', ')} alone — you already logged eating something in that slot, and swapping it now would rewrite the day's totals.`
                );
              }
              setPicking(null);
              refresh();
            } catch (e) {
              setPlanError(
                e instanceof ApiRequestError && e.body.detail
                  ? e.body.detail
                  : 'That did not save. Try again?'
              );
            }
          }}
        />
      )}

      {planningTraining && (
        <TrainingPlanner
          types={sessionTypes}
          date={planningTraining}
          onClose={() => setPlanningTraining(null)}
          onSaved={() => {
            setPlanningTraining(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- a day

function DayCard({
  day,
  slots,
  targets,
  isToday,
  sessionTypes,
  onPick,
  onPlanTraining,
  onChanged,
}: {
  day: WellnessWeekDay;
  slots: WellnessWeek['slots'];
  targets: WellnessWeek['targets'];
  isToday: boolean;
  sessionTypes: WellnessSessionTypeDef[];
  onPick: (slot: string) => void;
  onPlanTraining: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const bySlot = new Map(day.meals.map((m) => [m.slot, m]));
  const anything = day.meals.length > 0 || day.sessions.length > 0;

  const logIt = async (planId: number) => {
    setBusy(planId);
    try {
      await api.post(`/wellness/plan/${planId}/log`, {});
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const clearSlot = async (planId: number) => {
    setBusy(planId);
    try {
      await api.del(`/wellness/plan/${planId}`);
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  const completeSession = async (id: number) => {
    await api.put(`/wellness/sessions/${id}`, { completed: true, planned: false });
    onChanged();
  };

  return (
    <Card className={`p-4 sm:p-5 ${isToday ? 'border-sage' : ''}`}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-semibold text-ink">
          {DAY_NAME(day.date)} {DAY_NUM(day.date)}
          {isToday && <span className="ml-2 text-xs font-normal text-sage">Today</span>}
        </h2>
        {anything && day.planned.calories !== null && (
          <span className="text-xs text-ink-soft">
            planned {macro(day.planned.calories)} kcal
            {day.planned.protein_g !== null && ` · ${macro(day.planned.protein_g)}g protein`}
          </span>
        )}
      </div>

      <ul className="space-y-1.5">
        {slots.map((slot) => {
          const entry = bySlot.get(slot.slug);
          if (!entry)
            return (
              <li key={slot.slug}>
                <button
                  type="button"
                  onClick={() => onPick(slot.slug)}
                  className="flex min-h-11 w-full items-center gap-3 rounded-xl border border-dashed border-border px-3 text-left text-sm text-ink-faint hover:border-sage hover:text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                >
                  <span className="w-20 shrink-0 text-xs uppercase">{slot.label}</span>
                  <span>+ plan something</span>
                </button>
              </li>
            );

          const eaten = entry.logged_meal_id !== null;
          return (
            <li
              key={slot.slug}
              className="flex items-center gap-2 rounded-xl border border-border bg-canvas p-2"
            >
              <button
                type="button"
                onClick={() => onPick(slot.slug)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                {/* The photo, at the size where it does its job: recognising the meal from
                    across the kitchen rather than reading its name. */}
                <RecipePhoto
                  name={entry.recipe.name}
                  url={entry.recipe.photo_url}
                  className="size-12 shrink-0 rounded-xl"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.65rem] uppercase tracking-wide text-ink-faint">
                    {slot.label}
                  </span>
                  <span className="block truncate text-ink">{entry.recipe.name}</span>
                  <span className="block text-xs text-ink-soft">
                    {macro(entry.recipe.calories)} kcal · {macro(entry.recipe.protein_g, 'g')}{' '}
                    protein · {macro(entry.recipe.sodium_mg, 'mg')} sodium
                  </span>
                </span>
              </button>
              <span className="flex shrink-0 flex-col items-end gap-1">
                <button
                  type="button"
                  disabled={eaten || busy === entry.id}
                  onClick={() => logIt(entry.id)}
                  className={`min-h-11 rounded-full px-3 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                    eaten
                      ? 'bg-sage text-white'
                      : 'border border-sage bg-surface text-sage disabled:opacity-50'
                  }`}
                >
                  {eaten ? 'Ate it ✓' : busy === entry.id ? '…' : 'Ate it'}
                </button>
                {!eaten && (
                  <button
                    type="button"
                    onClick={() => clearSlot(entry.id)}
                    disabled={busy === entry.id}
                    className="min-h-6 px-2 text-xs text-ink-faint hover:text-clay focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                  >
                    Clear
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t border-border pt-3">
        {day.sessions.length === 0 ? (
          <button
            type="button"
            onClick={onPlanTraining}
            className="min-h-11 text-sm text-ink-faint hover:text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            + plan training
          </button>
        ) : (
          <ul className="space-y-1.5">
            {day.sessions.map((s) => {
              const def = sessionTypes.find((t) => t.slug === s.session_type);
              return (
                <li key={s.id} className="text-sm">
                  <div className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-xs text-ink-soft uppercase">Training</span>
                    <span className="min-w-0 flex-1 text-ink">
                      {def?.label ?? SESSION_LABELS[s.session_type] ?? s.session_type}
                      {s.duration_min ? (
                        <span className="text-ink-soft"> · {s.duration_min} min</span>
                      ) : null}
                    </span>
                    {s.completed ? (
                      <span className="shrink-0 text-xs text-sage">Done ✓</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => completeSession(s.id)}
                        className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-3 text-xs font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                      >
                        Did it
                      </button>
                    )}
                  </div>
                  {/* THE WORKOUT, not just its name. A planned session that only shows a
                      label leaves you standing in a gym wondering what "isometric" meant. */}
                  {def?.prescription && (
                    <p className="mt-1 ml-[5.75rem] text-xs text-ink-soft">{def.prescription}</p>
                  )}
                  {def?.zone_note && (
                    <p className="mt-0.5 ml-[5.75rem] text-xs text-ink-faint">{def.zone_note}</p>
                  )}
                </li>
              );
            })}
            <li>
              <button
                type="button"
                onClick={onPlanTraining}
                className="min-h-11 text-sm text-ink-faint hover:text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                + another
              </button>
            </li>
          </ul>
        )}
      </div>

      {day.planned.partial.length > 0 && (
        <p className="mt-2 text-xs text-honey">
          Planned totals are short — no {day.planned.partial.map(macroLabel).join(', ')} on some
          of what’s in these.
        </p>
      )}
      {targets.length > 0 && day.planned.sodium_mg !== null && (
        <SodiumNote planned={day.planned.sodium_mg} targets={targets} />
      )}
    </Card>
  );
}

/**
 * Sodium is called out on its own because it is the number the salt work turns on, and a
 * plan is the only place it can still be changed. States the comparison, recommends nothing.
 */
function SodiumNote({ planned, targets }: { planned: number; targets: WellnessWeek['targets'] }) {
  const target = Number(targets.find((t) => t.slug === 'sodium_mg')?.value);
  if (!Number.isFinite(target) || planned <= target) return null;
  return (
    <p className="mt-2 text-xs text-ink-soft">
      Planned sodium {macro(planned, 'mg')} is above the {target} mg target.
    </p>
  );
}

// ---------------------------------------------------------------- pickers

/**
 * Pick a recipe, then pick the days.
 *
 * Two steps rather than one grid of 28 buttons, because the two questions are genuinely
 * sequential — you decide what you're cooking, then how far it stretches. The day you tapped
 * arrives already ticked, so planning a single Tuesday dinner is still tap, tap, done.
 */
function PlanPicker({
  recipes,
  week,
  slot: initialSlot,
  date,
  onPlan,
  onClose,
}: {
  recipes: WellnessRecipe[];
  week: WellnessWeek;
  slot: string;
  date: string;
  onPlan: (recipeId: number, slot: string, dates: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState<WellnessRecipe | null>(null);
  const [slot, setSlot] = useState(initialSlot);
  const [dates, setDates] = useState<string[]>([date]);
  const [saving, setSaving] = useState(false);

  const shown = recipes.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  if (!chosen) {
    return (
      <Sheet title="Pick a meal" onClose={onClose}>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your recipes"
          className="mb-3 min-h-11 w-full rounded-xl border border-border bg-canvas px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        />
        {shown.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-ink-soft">
            {recipes.length === 0
              ? 'No recipes in your library yet — build one on the Meals tab.'
              : 'Nothing matches that.'}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {shown.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setChosen(r)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-canvas p-2 text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                >
                  <RecipePhoto name={r.name} url={r.photo_url} className="size-14 shrink-0 rounded-xl" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink">{r.name}</span>
                    <span className="block text-xs text-ink-soft">
                      {macro(r.calories)} kcal · {macro(r.protein_g, 'g')} protein ·{' '}
                      {macro(r.sodium_mg, 'mg')} sodium
                    </span>
                    {r.servings > 1 && (
                      <span className="block text-xs text-ink-faint">
                        makes {r.servings} — cook once, plan it across the days below
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
    );
  }

  const toggle = (d: string) =>
    setDates((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  return (
    <Sheet
      title="Which days?"
      onClose={onClose}
      footer={
        <button
          type="button"
          disabled={saving || dates.length === 0}
          onClick={async () => {
            setSaving(true);
            try {
              await onPlan(chosen.id, slot, dates);
            } finally {
              setSaving(false);
            }
          }}
          className="min-h-12 w-full rounded-full bg-sage px-4 font-medium text-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {saving
            ? 'Adding…'
            : dates.length === 1
              ? `Add to ${DAY_NAME(dates[0])}`
              : `Add to ${dates.length} days`}
        </button>
      }
    >
      <div className="mb-4 flex items-center gap-3">
        <RecipePhoto name={chosen.name} url={chosen.photo_url} className="size-14 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-ink">{chosen.name}</p>
          <p className="text-xs text-ink-soft">
            {macro(chosen.calories)} kcal · {macro(chosen.sodium_mg, 'mg')} sodium per serving
          </p>
        </div>
        <button
          type="button"
          onClick={() => setChosen(null)}
          className="min-h-11 shrink-0 rounded-full px-3 text-sm text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          Change
        </button>
      </div>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium text-ink">Slot</legend>
        <div className="flex flex-wrap gap-2">
          {week.slots.map((s) => (
            <button
              key={s.slug}
              type="button"
              onClick={() => setSlot(s.slug)}
              aria-pressed={slot === s.slug}
              className={`min-h-11 rounded-full px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                slot === s.slug ? 'bg-sage text-white' : 'border border-border bg-canvas text-ink-soft'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-ink">Days</legend>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
          {week.days.map((d) => {
            const on = dates.includes(d.date);
            const taken = d.meals.find((m) => m.slot === slot);
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => toggle(d.date)}
                aria-pressed={on}
                className={`flex min-h-16 flex-col items-center justify-center rounded-xl border px-1 py-2 text-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                  on ? 'border-sage bg-sage text-white' : 'border-border bg-canvas text-ink-soft'
                }`}
              >
                <span className="font-medium">{DAY_NAME(d.date)}</span>
                <span className={on ? 'text-white/80' : 'text-ink-faint'}>{DAY_NUM(d.date)}</span>
                {/* Says what is already there, so replacing something is a choice rather
                    than a surprise found later in the week. */}
                {taken && (
                  <span className={`mt-0.5 w-full truncate ${on ? 'text-white/80' : 'text-ink-faint'}`}>
                    {taken.logged_meal_id !== null ? 'eaten' : taken.recipe.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-ink-soft">
          A day that already has something in this slot gets replaced. A day you already logged
          eating is left alone.
        </p>
      </fieldset>
    </Sheet>
  );
}

function TrainingPlanner({
  date,
  types,
  onClose,
  onSaved,
}: {
  date: string;
  types: WellnessSessionTypeDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<string>(types[0]?.slug ?? 'strength');
  const [duration, setDuration] = useState('');
  const [saving, setSaving] = useState(false);
  const def = types.find((t) => t.slug === type);
  // The doc's own duration for the chosen session, so the common case is zero typing.
  const durationValue = duration || (def?.default_duration_min ? String(def.default_duration_min) : '');

  return (
    <Sheet title={`Plan training — ${DAY_NAME(date)} ${DAY_NUM(date)}`} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setSaving(true);
          try {
            await api.post('/wellness/sessions', {
              session_date: date,
              session_type: type,
              planned: true,
              completed: false,
              duration_min: durationValue,
            });
            onSaved();
          } finally {
            setSaving(false);
          }
        }}
      >
        <div className="flex flex-wrap gap-2">
          {types.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => {
                setType(t.slug);
                setDuration('');
              }}
              aria-pressed={type === t.slug}
              className={`min-h-11 rounded-full px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                type === t.slug ? 'bg-sage text-white' : 'border border-border bg-canvas text-ink-soft'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Shown BEFORE saving, so the choice is informed rather than a guess at a word. */}
        {def?.prescription && (
          <div className="rounded-xl bg-canvas px-3 py-2">
            <p className="text-sm text-ink">{def.prescription}</p>
            {def.zone_note && <p className="mt-1 text-xs text-ink-soft">{def.zone_note}</p>}
          </div>
        )}
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Minutes</span>
          <input
            value={durationValue}
            onChange={(e) => setDuration(e.target.value)}
            inputMode="numeric"
            placeholder="45"
            className="min-h-11 w-full rounded-xl border border-border bg-canvas px-3 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          className="min-h-12 w-full rounded-full bg-sage px-4 font-medium text-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {saving ? 'Saving…' : 'Add to the plan'}
        </button>
      </form>
    </Sheet>
  );
}
